import React, { useEffect, useState } from "react";
import axiosInstance from "../components/api/axiosInstance";
import PortalAlert from "./PortalAlert";
import { PASSWORD_POLICY_HINT, validatePasswordStrength } from "../utils/passwordPolicy";
import PasswordInput from "../components/PasswordInput";

function TenantChangePassword() {
  const [hasPassword, setHasPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/client/me");
        if (!active) return;
        setHasPassword(!!res.data?.data?.Has_Password);
      } catch (_) {
        if (active) setPwMsg({ type: "error", text: "Could not load account details." });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const savePassword = async (e) => {
    e.preventDefault();
    setPwMsg({ type: "", text: "" });
    const strengthError = validatePasswordStrength(pw.new_password);
    if (strengthError) {
      setPwMsg({ type: "error", text: strengthError });
      return;
    }
    if (pw.new_password !== pw.confirm) {
      setPwMsg({ type: "error", text: "Passwords do not match." });
      return;
    }
    setSavingPw(true);
    try {
      await axiosInstance.post("/client/set-password", {
        current_password: pw.current_password || undefined,
        new_password: pw.new_password,
      });
      setPwMsg({ type: "success", text: "Password updated." });
      setHasPassword(true);
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setPwMsg({ type: "error", text: err.response?.data?.message || "Could not update password." });
    } finally {
      setSavingPw(false);
    }
  };

  if (loading) {
    return <p className="cportal-muted">Loading…</p>;
  }

  return (
    <form className="cportal-card" onSubmit={savePassword}>
      {hasPassword ? null : (
        <p className="cportal-muted cportal-password-intro">
          Set a password to sign in with your company email.
        </p>
      )}
      <PortalAlert
        type={pwMsg.type === "error" ? "error" : "success"}
        message={pwMsg.text}
        onClear={() => setPwMsg({ type: "", text: "" })}
      />

      <div className="cportal-form-grid">
        {hasPassword && (
          <div className="cportal-field">
            <label>Current password</label>
            <PasswordInput
              value={pw.current_password}
              onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))}
            />
          </div>
        )}
        <div className="cportal-field">
          <label>New password</label>
          <PasswordInput
            value={pw.new_password}
            onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))}
          />
          <p className="cportal-muted" style={{ marginTop: 6, marginBottom: 0 }}>
            {PASSWORD_POLICY_HINT}
          </p>
        </div>
        <div className="cportal-field">
          <label>Confirm new password</label>
          <PasswordInput
            value={pw.confirm}
            onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
          />
        </div>
      </div>

      <div className="cportal-form-actions">
        <button type="submit" className="cportal-btn cportal-btn-primary" disabled={savingPw}>
          {savingPw ? "Saving…" : hasPassword ? "Change password" : "Set password"}
        </button>
      </div>
    </form>
  );
}

export default TenantChangePassword;
